import type { CalendarTheme, CalendarStyleConfig, CalendarBehaviorConfig } from './types';

/**
 * Default calendar theme with color scheme and typography
 */
export const DEFAULT_THEME: CalendarTheme = {
  backgroundColor: '#ffffff',
  headerTextColor: '#2d2d2d',
  dayNumberColor: '#2d2d2d',
  disabledDayColor: '#c0c0c0',
  borderColor: '#d8e7fb',
  disabledBackgroundColor: '#fafafa',
  eventColors: {
    pending: '#4caf50',
    done: '#cccccc',
    default: '#ffc107',
  },
};

/**
 * Dark theme calendar colors - for use in dark mode
 * Optimized for visibility in dark backgrounds
 * 
 * Layout:
 * - Current month cells: dark gray background (#262626)
 * - Previous/next month cells: gray background (#333333) - changed from white
 */
export const DARK_THEME: CalendarTheme = {
  backgroundColor: '#1a1a1a',
  surfaceColor: '#262626', // Dark gray for current month cells
  headerTextColor: '#ffffff',
  dayNumberColor: '#ffffff',
  disabledDayColor: '#666666',
  disabledBackgroundColor: '#333333', // GRAY background for previous/next month cells (was white in light mode)
  borderColor: '#404040',
  controlBackgroundColor: '#1A1A1A',
  controlActiveBackgroundColor: '#316BB8',
  controlTextColor: '#F2F2F2',
  controlActiveTextColor: '#ffffff',
  controlBorderColor: '#3A3A3A',
  chevronColor: '#ffffff',
  todayAccentColor: '#4a9eff',
  overflowBadgeBackgroundColor: '#1E2E44',
  overflowBadgeTextColor: '#ffffff',
  shadowColor: 'rgba(176, 176, 176, 0.2)',
  eventColors: {
    pending: '#4caf50',
    done: '#888888',
    default: '#ffc107',
  },
};

/**
 * Default styling configuration
 */
export const DEFAULT_STYLE_CONFIG: CalendarStyleConfig = {
  // Event badge styling
  eventBadgeHeight: 24,
  eventBadgeRadius: 4,
  eventBadgeFontSize: 12,
  
  // Day cell styling
  dayCellMinHeight: 80,
  dayCellBorderWidth: 1,
  dayCellRadius: 8,
  dayCellPadding: 8,
  
  // Day number styling
  dayNumberFontSize: 14,
  dayNumberFontWeight: '500',
  todayIndicatorHeight: 2,
  todayIndicatorColor: '#034895',
  
  // Modal and popover styling
  modalBorderRadius: 8,
  modalBackdropOpacity: 0.5,
  popoverWidth: '85%',
  popoverMaxHeight: '80%',
  
  // Badge for overflow indicator
  overflowBadgeBackgroundColor: '#e8f0ff',
  overflowBadgeBorderRadius: 12,
  overflowBadgePadding: 8,
  overflowBadgeFontSize: 12,
};

/**
 * Default behavior configuration
 */
export const DEFAULT_BEHAVIOR_CONFIG: CalendarBehaviorConfig = {
  enableEventRemoval: true,
  enableViewModeSwitch: true,
  enableWeekdayHeaders: true,
  singleEventClickBehavior: 'both',
  multipleEventClickBehavior: 'both',
  enableAnimations: true,
  enableHapticFeedback: false,
};

/**
 * Weekday labels
 */
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Color constants
 */
export const COLORS = {
  primary: '#034895',
  primaryLight: '#1976d2',
  text: '#2d2d2d',
  textSecondary: '#3a3a3a',
  border: '#d8e7fb',
  background: '#ffffff',
  backgroundLight: '#eff5fc',
  backgroundLightAlt: '#fbfcfe',
  backgroundDisabled: '#fafafa',
  disabledText: '#c0c0c0',
  success: '#4caf50',
  done: '#cccccc',
  warning: '#ffc107',
  dark: '#666666',
};

/**
 * Border and spacing constants
 */
export const SPACING = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 20,
};

export const BORDER_RADIUS = {
  sm: 4,
  md: 8,
  lg: 14,
};
